import React from "react";
import SeoLandingLayout from "./SeoLandingLayout";
import { Dices, Swords, Shield, Users, Sparkles, ScrollText } from "lucide-react";
import { NARRATOR_LIST } from "@/data/models";

const FEATURES = [
  { icon: Dices, title: "D&D-inspired dice, when you want them", body: "Occasional dice rolls with advantage/disadvantage and saving throws when an action could plausibly fail. Light flavour, not a full tabletop simulation." },
  { icon: Shield, title: "HP & status effects", body: "A simple combat tracker in the sidebar for HP and active status effects during dangerous scenes." },
  { icon: Swords, title: "Attacks & saves", body: "A longsword rolls to hit and rolls damage; a save can catch you at the last second on a botched leap. Spell slots are tracked where a character has them." },
  { icon: Users, title: "Party companions", body: "Recruit NPCs mid-adventure. They have their own HP and can take part in scenes and combat alongside you." },
  { icon: Sparkles, title: "Switchable AI narrators", body: `Choose from ${NARRATOR_LIST}. Switch mid-saga while the Memory Codex carries story details forward.` },
  { icon: ScrollText, title: "Memory Codex", body: "Named characters, quests and factions are tracked and fed back to the narrator to improve continuity across long sagas." },
];

const FAQ = [
  {
    q: "Is this a full D&D 5e simulator?",
    a: "No. TalesTalesTales uses D&D-inspired flavour — occasional dice, HP, saving throws — as light structure, not a complete rules-compatible tabletop simulation. It isn't licensed by or affiliated with Wizards of the Coast."
  },
  {
    q: "Do I need to know the rules?",
    a: "No. The narrator asks for the right roll when it's needed and explains what's happening. If you're new to tabletop games, you'll learn as you go; if you're a veteran, the flavour will feel familiar."
  },
  {
    q: "Can I play solo?",
    a: "Yes — TalesTalesTales is designed for solo play. The narrator handles the world and every NPC. No scheduling required."
  },
  {
    q: "How is this different from an AI Dungeon-style chatbot?",
    a: "The Memory Codex tracks named characters, quests, threads and inventory changes and feeds them back to the narrator, which improves continuity over long sagas compared with a plain chat log. You can also treat it as a novel — the story is illustrated and written in literary prose."
  },
  {
    q: "What happens if my character is defeated?",
    a: "Permadeath is opt-in. By default, falling to 0 HP triggers a death-save sequence where genuinely implemented; three failures ends the saga."
  },
];

export default function AiDndGame() {
  return (
    <SeoLandingLayout
      testId="seo-ai-dnd-game-page"
      path="/ai-dnd-game"
      title="AI D&D-Style Adventure Game | TalesTalesTales"
      description="A browser-based AI D&D-style adventure: turn-based prose with occasional dice rolls, skills, equipment and inventory. Light RPG flavour, no rulebook needed."
      eyebrow="AI D&D-Style Adventure"
      h1="AI D&D-style adventure"
      h1Accent="in your browser"
      lede="TalesTalesTales is an interactive AI Novel with light D&D-inspired RPG flavour. Occasional dice rolls, HP tracking, and party companions add structure when you want it — the story is always the point."
      body={(
        <>
          <p>
            If you want the feel of a tabletop session without running every rule yourself, this fits. Type
            an action, the narrator resolves the outcome, and asks for a roll when the result could plausibly
            go either way. HP and active status effects show in a simple sidebar tracker during dangerous scenes.
          </p>
          <p>
            The rules layer is <strong>opt-in and light</strong> — flavour and structure, not a deep tabletop
            simulation. You can slide the dice off entirely and treat the AI as a novel co-writer, or leave them
            on for occasional rolls, saves, and party companions.
          </p>
          <p>
            The <strong>Memory Codex</strong> is what helps long campaigns stay consistent. Named characters,
            factions and unresolved threads are tracked and fed back to the narrator each turn, which improves
            continuity — it does not guarantee perfect recall over very long sagas. Party companions are tracked
            individually with their own HP.
          </p>
          <p>
            Every scene is auto-illustrated by an image model tuned for the setting. You get a picture
            of your character, your party, your surroundings. It looks like a novel. Switch AI narrators
            (Claude Sonnet 4.5, Claude Haiku 4.5, GPT-5, Gemini 2.5 Pro) mid-saga while the Memory Codex
            carries story details across the swap.
          </p>
        </>
      )}
      features={FEATURES}
      faq={FAQ}
    />
  );
}
